import { createSignal, For, Show, onMount, onCleanup, type Component } from 'solid-js';
import DOMPurify from 'dompurify';
import { debug, error as logError } from '@utils/console';
import { persistantStore } from '@store/persistantStore';
import { buildAskContext } from '@store/assistantContextStore';
import {
  askAssistantStream,
  cancelAsk,
  listConversations,
  loadConversation,
  rememberFinding,
  sendFeedback,
  type AssistantAnswer,
} from '@utils/assistantApi';
import {
  applyAssistantUiActions,
  currentUiActionFingerprint,
  type AssistantUiAction,
} from '@utils/assistantUiActions';
import '@styles/Assistant.css';

export type CapitanLaunchParams = {
  page_name?: string | null;
  menu_name?: string | null;
  project_id?: number;
};

type ChatMessage = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  messageId?: string;
  toolsUsed?: string[];
  costUsd?: number | null;
  clarify?: { question: string; options: string[] } | null;
  uiActions?: AssistantUiAction[];
};

function simpleMarkdownToHtml(text: string): string {
  let html = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  html = html.replace(/\|(.+)\|\n\|[-| :]+\|\n((?:\|.+\|\n?)*)/g, (_m, header: string, body: string) => {
    const th = header
      .split('|')
      .map((c) => c.trim())
      .filter(Boolean)
      .map((c) => `<th>${c}</th>`)
      .join('');
    const rows = body
      .trim()
      .split('\n')
      .map((row) => {
        const cells = row
          .split('|')
          .map((c) => c.trim())
          .filter(Boolean)
          .map((c) => `<td>${c}</td>`)
          .join('');
        return `<tr>${cells}</tr>`;
      })
      .join('');
    return `<table><thead><tr>${th}</tr></thead><tbody>${rows}</tbody></table>`;
  });
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/`([^`]+)`/g, '<code>$1</code>');
  html = html.replace(/\n/g, '<br/>');
  return DOMPurify.sanitize(html);
}

export type CapitanPanelProps = {
  isInSplitView?: boolean;
  launchParams?: CapitanLaunchParams;
};

const CapitanPanel: Component<CapitanPanelProps> = (props) => {
  const [messages, setMessages] = createSignal<ChatMessage[]>([]);
  const [draft, setDraft] = createSignal('');
  const [busy, setBusy] = createSignal(false);
  const [statusLabel, setStatusLabel] = createSignal('');
  const [activity, setActivity] = createSignal<string[]>([]);
  const [conversationId, setConversationId] = createSignal<string | null>(null);
  const [history, setHistory] = createSignal<Array<{ conversation_id: string; title?: string }>>([]);
  let abortCtrl: AbortController | null = null;
  let askId: string | null = null;
  const fingerprint = currentUiActionFingerprint();

  const projectId = () =>
    props.launchParams?.project_id || Number(persistantStore.selectedProjectId()) || 0;
  const menuName = () =>
    props.launchParams?.menu_name || persistantStore.selectedMenu() || null;
  const pageName = () =>
    props.launchParams?.page_name || persistantStore.selectedPage() || menuName();

  const refreshHistory = async () => {
    const pid = projectId();
    if (!pid) return;
    try {
      const rows = (await listConversations(pid)) as Array<{
        conversation_id: string;
        title?: string;
      }>;
      setHistory(rows);
    } catch (err) {
      debug('[Capitan] history load failed', (err as Error)?.message);
    }
  };

  onMount(() => {
    void refreshHistory();
  });

  onCleanup(() => {
    abortCtrl?.abort();
  });

  const pushActivity = (line: string) => {
    setActivity((prev) => [...prev.slice(-5), line]);
  };

  const submit = async (questionOverride?: string) => {
    const question = (questionOverride ?? draft()).trim();
    if (!question || busy()) return;
    const pid = projectId();
    if (!pid) {
      setStatusLabel('Select a project first.');
      return;
    }

    setDraft('');
    setBusy(true);
    setStatusLabel('Thinking…');
    setActivity([]);
    askId = crypto.randomUUID();
    abortCtrl = new AbortController();

    setMessages((prev) => [
      ...prev,
      { id: crypto.randomUUID(), role: 'user', content: question },
    ]);

    try {
      const context = buildAskContext({
        menu_name: menuName(),
        page_name: pageName(),
        project_id: pid,
      });
      const answer = await askAssistantStream(
        {
          question,
          conversation_id: conversationId(),
          page_name: pageName(),
          menu_name: menuName(),
          project_id: pid,
          context,
          ask_id: askId,
        },
        {
          onProgress: (event, data) => {
            if (event === 'phase') {
              const phase = (data as { phase?: string })?.phase || '';
              setStatusLabel(phase || 'Working…');
            } else if (event === 'step') {
              const tool = (data as { tool?: string })?.tool || 'tool';
              pushActivity(`Calling ${tool}…`);
              setStatusLabel(`Calling ${tool}…`);
            } else if (event === 'step_done') {
              const tool = (data as { tool?: string })?.tool || 'tool';
              pushActivity(`Done: ${tool}`);
            } else if (event === 'note') {
              pushActivity(String((data as { text?: string })?.text || data));
            }
          },
          onError: (msg) => {
            setStatusLabel(msg);
          },
        },
        abortCtrl.signal
      );

      if (answer) {
        handleAnswer(answer);
      } else {
        setStatusLabel('No answer received.');
      }
    } catch (err) {
      if ((err as Error)?.name === 'AbortError') {
        setStatusLabel('Stopped.');
      } else {
        logError('[Capitan] ask failed', err);
        setStatusLabel((err as Error)?.message || 'Ask failed');
        setMessages((prev) => [
          ...prev,
          {
            id: crypto.randomUUID(),
            role: 'assistant',
            content: `Sorry — ${(err as Error)?.message || 'request failed'}.`,
          },
        ]);
      }
    } finally {
      setBusy(false);
      abortCtrl = null;
      askId = null;
    }
  };

  const handleAnswer = (answer: AssistantAnswer) => {
    setConversationId(answer.conversation_id);
    setMessages((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: answer.answer,
        messageId: answer.message_id,
        toolsUsed: answer.tools_used,
        costUsd: answer.cost?.usd ?? null,
        clarify: answer.clarify || null,
        uiActions: (answer.ui_actions as AssistantUiAction[]) || [],
      },
    ]);
    setStatusLabel('');
    applyAssistantUiActions(answer.ui_actions as AssistantUiAction[], fingerprint);
    void refreshHistory();
  };

  const stop = async () => {
    abortCtrl?.abort();
    if (askId) await cancelAsk(askId, projectId());
    setBusy(false);
    setStatusLabel('Stopped.');
  };

  const newChat = () => {
    setConversationId(null);
    setMessages([]);
    setStatusLabel('');
    setActivity([]);
  };

  const onSelectHistory = async (id: string) => {
    if (!id) return;
    const data = await loadConversation(projectId(), id);
    if (!data) return;
    setConversationId(id);
    const msgs = (data.messages || []) as Array<{
      message_id: string;
      role: string;
      content: string;
      tools_used?: string[];
      cost?: number;
      ui_actions?: AssistantUiAction[];
    }>;
    setMessages(
      msgs
        .filter((m) => m.role === 'user' || m.role === 'assistant')
        .map((m) => ({
          id: m.message_id,
          role: m.role as 'user' | 'assistant',
          content: m.content,
          messageId: m.message_id,
          toolsUsed: m.tools_used,
          costUsd: m.cost ?? null,
          uiActions: m.ui_actions,
        }))
    );
  };

  return (
    <div class={`capitan-panel ${props.isInSplitView ? 'capitan-panel--split' : ''}`}>
      <div class="capitan-compact-bar">
        <span class="capitan-compact-bar__title">Capitan</span>
        <button type="button" onClick={newChat} title="New chat">
          New
        </button>
        <select
          class="capitan-history-select"
          onChange={(e) => void onSelectHistory(e.currentTarget.value)}
          value={conversationId() || ''}
        >
          <option value="">History</option>
          <For each={history()}>
            {(h) => (
              <option value={h.conversation_id}>{h.title || h.conversation_id.slice(0, 8)}</option>
            )}
          </For>
        </select>
      </div>

      <div class="capitan-thread">
        <Show when={messages().length === 0}>
          <div class="capitan-greeting">
            Ask Capitan about this day, race results, wind-band performance, or what worked in similar
            conditions. Answers stay grounded in RaceSight data.
          </div>
        </Show>
        <For each={messages()}>
          {(m) => (
            <div class={`capitan-bubble capitan-bubble--${m.role}`}>
              <Show
                when={m.role === 'assistant'}
                fallback={<div class="capitan-bubble__body">{m.content}</div>}
              >
                <div
                  class="capitan-bubble__body"
                  // eslint-disable-next-line solid/no-innerhtml
                  innerHTML={simpleMarkdownToHtml(m.content)}
                />
              </Show>
              <Show when={m.role === 'assistant' && m.messageId}>
                <div class="capitan-answer-actions">
                  <button
                    type="button"
                    onClick={() =>
                      void sendFeedback({
                        projectId: projectId(),
                        messageId: m.messageId!,
                        rating: 'up',
                      })
                    }
                  >
                    Helpful
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      void rememberFinding({
                        projectId: projectId(),
                        messageId: m.messageId!,
                      })
                    }
                  >
                    Remember this
                  </button>
                  <Show when={m.uiActions?.length}>
                    <button
                      type="button"
                      onClick={() => applyAssistantUiActions(m.uiActions, fingerprint)}
                    >
                      Highlight events
                    </button>
                  </Show>
                </div>
              </Show>
              <Show when={m.clarify?.options?.length}>
                <div class="capitan-clarify">
                  <For each={m.clarify!.options}>
                    {(opt) => (
                      <button type="button" onClick={() => void submit(opt)}>
                        {opt}
                      </button>
                    )}
                  </For>
                </div>
              </Show>
              <Show when={m.toolsUsed?.length || m.costUsd != null}>
                <div class="capitan-bubble__meta">
                  <Show when={m.toolsUsed?.length}>tools: {m.toolsUsed!.join(', ')} · </Show>
                  <Show when={m.costUsd != null}>~${m.costUsd!.toFixed(4)}</Show>
                </div>
              </Show>
            </div>
          )}
        </For>
      </div>

      <Show when={busy() || statusLabel() || activity().length}>
        <div class="capitan-status">
          <div>{statusLabel()}</div>
          <Show when={activity().length}>
            <ul class="capitan-status__activity">
              <For each={activity()}>{(a) => <li>{a}</li>}</For>
            </ul>
          </Show>
        </div>
      </Show>

      <div class="capitan-composer">
        <textarea
          value={draft()}
          placeholder="Ask Capitan…"
          disabled={busy()}
          onInput={(e) => setDraft(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void submit();
            }
          }}
        />
        <Show
          when={!busy()}
          fallback={
            <button type="button" onClick={() => void stop()}>
              Stop
            </button>
          }
        >
          <button type="button" class="capitan-composer__send" onClick={() => void submit()}>
            Ask
          </button>
        </Show>
      </div>
    </div>
  );
};

export default CapitanPanel;
